/*
 * header-nav-fit.js — hands the desktop nav row over to the drawer when it stops fitting.
 *
 * assets/section-header.css already does the ordinary work: the logo shrinks from 210px
 * to a 130px floor as the bar narrows, and below 1270px the inline menu is swapped for
 * <header-drawer>. Those numbers are measured against the German main menu as it stands
 * today, and both halves of that move: the menu is edited in the theme editor, and the
 * labels are translated (the English storefront went live 01.09.). This script is the
 * part that cannot be written as a breakpoint — it measures the row that is actually
 * rendered and collapses it when the arithmetic says it will not fit, whatever the
 * language or the item count.
 *
 * Below 1270px it has nothing to do: the media query has already hidden the row, and this
 * class only ever hides, never reveals.
 */

const NAV_FIT_MIN_WIDTH = 1270;

const COLLAPSED_CLASS = 'header-wrapper--nav-collapsed';
const MEASURING_CLASS = 'header-wrapper--nav-measuring';

class HeaderNavFit {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.bar = wrapper.querySelector('.header__bar');
    this.nav = wrapper.querySelector('.header__inline-menu');
    this.list = this.nav && this.nav.querySelector('.list-menu--inline');
    this.logo = wrapper.querySelector('.header__logo');
    this.icons = wrapper.querySelector('.header__icons');

    if (!this.bar || !this.nav || !this.list || !this.logo || !this.icons) return;

    this.frame = null;
    this.lastWidth = null;
    this.schedule = this.schedule.bind(this);
    this.remeasure = this.remeasure.bind(this);

    this.observer = new ResizeObserver(this.schedule);
    this.observer.observe(this.bar);

    /* Rethink Sans arrives after first paint, and the fallback measures narrower —
       without this the row is sized from the wrong font and stays a little too eager
       to keep the nav. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.remeasure);
    }

    /* Synchronously, not through schedule(): rAF does not run in a background tab, so
       a page opened in one (cmd-click, a restored session) would carry no verdict at
       all until it was looked at. Layout is available by the time this deferred script
       runs, so the first answer costs one forced reflow and is correct before first
       paint. Every later one goes through the observer and its rAF. */
    this.update();
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  /* Same pass, but with the "nothing has changed" shortcut in update() disarmed —
     for the things that change the answer without changing the bar's width. */
  remeasure() {
    this.lastWidth = null;
    this.schedule();
  }

  update() {
    /* Collapsing shortens the bar (one line of nav less) and the measuring pass moves
       the row out of flow, and BOTH are resizes of the observed element: without this
       the observer would re-enter its own callback for ever. The bar's width is set by
       the viewport, never by what is inside it, so width is the only input that can
       actually change the verdict. */
    const width = this.bar.clientWidth;
    if (width === this.lastWidth) return;
    this.lastWidth = width;

    if (window.innerWidth < NAV_FIT_MIN_WIDTH) {
      /* The media query owns this range. Drop the class so the range above it is
         re-decided from scratch on the way back out, rather than from a stale verdict. */
      this.wrapper.classList.remove(COLLAPSED_CLASS);
      return;
    }

    const { needed, available } = this.measure();
    this.wrapper.classList.toggle(COLLAPSED_CLASS, needed > available);
  }

  /*
   * One synchronous pass with the collapse undone and the row taken out of flow, so
   * every number is the one the EXPANDED header would have: the logo at the width the
   * clamp gives it rather than the 210px the collapsed state pins it to, and the list at
   * its natural width rather than the width flexbox squeezed it into. Measuring in the
   * current state instead would make the two verdicts disagree and the header flap
   * between them as the window resizes across the threshold.
   */
  measure() {
    const wasCollapsed = this.wrapper.classList.contains(COLLAPSED_CLASS);
    this.wrapper.classList.remove(COLLAPSED_CLASS);
    this.wrapper.classList.add(MEASURING_CLASS);

    const styles = getComputedStyle(this.bar);
    const available =
      this.bar.clientWidth - parseFloat(styles.paddingInlineStart) - parseFloat(styles.paddingInlineEnd);

    /* The hamburger is deliberately left out: it is only in the bar while the nav is
       collapsed, so counting it would make collapsing self-justifying. `.header__icons`
       carries its own 24px lead-in padding, which is the gap before the icon cluster. */
    const needed =
      this.logo.getBoundingClientRect().width +
      parseFloat(getComputedStyle(this.nav).marginInlineStart) +
      this.list.getBoundingClientRect().width +
      this.icons.getBoundingClientRect().width;

    this.wrapper.classList.remove(MEASURING_CLASS);
    if (wasCollapsed) this.wrapper.classList.add(COLLAPSED_CLASS);

    return { needed: Math.ceil(needed), available: Math.floor(available) };
  }
}

const instances = new WeakMap();

function initHeaderNavFit(root = document) {
  root.querySelectorAll('.header-wrapper').forEach((wrapper) => {
    if (instances.has(wrapper)) return;
    instances.set(wrapper, new HeaderNavFit(wrapper));
  });
}

initHeaderNavFit();

/* Theme editor: the section is replaced wholesale, so the new bar has never been
   measured. The instance for the old one goes with the element it observed. */
document.addEventListener('shopify:section:load', (event) => {
  initHeaderNavFit(event.target);
});
